/**
 * Web addresses reach us in every shape a person can type one: with a scheme or without,
 * with `www.` or without, with a trailing slash or without. Stears Core stores whatever
 * it was given, so the same address reads as `https://aruwacapital.com/` here and as
 * `aruwacapital.com/` on the Stears platform.
 *
 * Two helpers settle that. `absoluteUrl` is the address we link to and store — always
 * absolute, so a browser can follow it. `displayUrl` is the address we show — the domain
 * and path alone, matching how the platform renders the same value.
 */

// Any scheme, not just `x://`: `mailto:` and `javascript:` have to be recognised so the
// protocol check below can reject them rather than have https:// pasted in front.
// The dot is left out so `aruwacapital.com:8080` reads as a host and port, not a scheme.
const SCHEME = /^[a-z][a-z0-9+-]*:/i

/**
 * The address to link to, or null when the value is not a web address at all. A bare
 * domain is assumed to be https: that is what a person typing `aruwacapital.com` means,
 * and it is what every browser does with the same input.
 */
export function absoluteUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (trimmed === '') return null

  let parsed: URL
  try {
    parsed = new URL(SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`)
  } catch {
    return null
  }
  // mailto:, javascript: and the rest are not things we render as a website link.
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
  // `https://localhost` parses, but a dotless host in this data is a typo, not an address.
  if (!parsed.hostname.includes('.')) return null
  return parsed.toString()
}

/**
 * What to store for an address someone has just finished typing: their own text, with
 * the scheme filled in if they left it off. Nothing else is rewritten — an address that
 * already works is theirs, not ours to reshape into a canonical form under them.
 */
export function tidyUrl(value: string): string {
  const trimmed = value.trim()
  if (trimmed === '' || SCHEME.test(trimmed) || absoluteUrl(trimmed) === null) return trimmed
  return `https://${trimmed}`
}

/** Keeps a long path readable without hiding either end of it. */
function truncateMiddle(text: string, max: number): string {
  if (text.length <= max) return text
  const head = Math.ceil((max - 1) / 2)
  return `${text.slice(0, head)}…${text.slice(text.length - (max - 1 - head))}`
}

/**
 * How the address reads on screen: no scheme, no `www.`, no trailing slash — the form
 * the Stears platform uses. The full address still goes in the link, so nothing is lost
 * by shortening what is shown. Anything that is not a web address is returned as typed.
 */
export function displayUrl(value: unknown, maxLength = 48): string {
  const absolute = absoluteUrl(value)
  if (!absolute) return typeof value === 'string' ? value.trim() : ''

  const parsed = new URL(absolute)
  const host = parsed.host.replace(/^www\./i, '')
  const path = `${parsed.pathname}${parsed.search}${parsed.hash}`.replace(/\/$/, '')
  const full = `${host}${path}`
  if (full.length <= maxLength) return full

  // The domain is the part that identifies the link, so it never gets shortened.
  const room = maxLength - host.length
  return room < 8 ? host : `${host}${truncateMiddle(path, room)}`
}
